'use client';

import * as React from 'react';
import { Button } from '@/components/ui/button';

type CalcState = {
  emp: number;
  current: number;
  ppm: number;
  events: number;
  eventFee: number;
  fixed: number;
  edo: boolean;
  zup: boolean;
  sla: boolean;
};

const cf = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

function useCalc(defaults: CalcState) {
  const [s, setS] = React.useState<CalcState>(() => {
    if (typeof window === 'undefined') {
      return defaults;
    }
    try {
      const raw = localStorage.getItem('outstaff_calc');
      return raw
        ? { ...defaults, ...JSON.parse(raw) }
        : defaults;
    } catch {
      return defaults;
    }
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('outstaff_calc', JSON.stringify(s));
    }
  }, [s]);

  return { s, setS };
}

function Section({
  id, gray, className, children,
}: React.PropsWithChildren<{ id?: string; gray?: boolean; className?: string }>) {
  return (
    <section id={id} className={`py-20 box-border ${gray ? 'bg-slate-50' : ''} ${className ?? ''}`}>
      <div className="mx-auto max-w-6xl px-6">{children}</div>
    </section>
  );
}

function Card({ className, children }: React.PropsWithChildren<{ className?: string }>) {
  return <div className={`rounded-2xl bg-white p-6 shadow-[0_6px_18px_rgba(0,0,0,0.06)] ${className ?? ''}`}>{children}</div>;
}

function KPI({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-white backdrop-blur h-[72px]">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-base">{icon}</div>
      <div>
        <div className="text-xl font-extrabold leading-none">{value}</div>
        <div className="text-sm opacity-90 leading-none">{label}</div>
      </div>
    </div>
  );
}

function LabeledInputNumber(props: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  step?: number;
}) {
  const { label, value, onChange, min, step = 1 } = props;
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      />
    </label>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-extrabold text-[#003B9B]">{value}</div>
    </Card>
  );
}

export default function OutstaffingFunctionsPage() {
  const defaults = {
    emp: 200, current: 600_000, ppm: 550, events: 120, eventFee: 220, fixed: 20_000,
    edo: true, zup: false, sla: true,
  };
  const { s, setS } = useCalc(defaults);

  const [formData, setFormData] = React.useState({
    name: '',
    phone: '',
    userFunctions: '',
    email: '',
    agreed: false,
  });

  const [formStatus, setFormStatus] = React.useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAgreementChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, agreed: checked }));
  };

  const getStatusClasses = (status: string) => {
    if (status.startsWith('❌')) {
      return 'mt-2 text-sm font-medium text-red-600';
    }
    if (status.startsWith('✅')) {
      return 'mt-2 text-sm font-medium text-green-600';
    }
    return 'mt-2 text-sm text-slate-500';
  };


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.agreed) {
      setFormStatus('❌ Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    if (!formData.name || !formData.phone || !formData.email) {
      setFormStatus('❌ Пожалуйста, заполните имя, телефон и email.');
      return;
    }

    setFormStatus('Отправка...');

    const { agreed, ...dataToSend } = formData;
    const payload = { ...dataToSend, formName: "Аутстаффинг функций" };

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/outstaffing-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });

      if (response.ok) {
        setFormStatus('✅ Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', userFunctions: '', email: '', agreed: false });
      } else {
        const errorData = await response.json();
        setFormStatus(`❌ Произошла ошибка: ${errorData.error?.message || 'Не удалось отправить заявку.'}`);
        console.error(errorData);
      }
    } catch (error) {
      setFormStatus('❌ Ошибка сети. Пожалуйста, проверьте ваше подключение.');
      console.error(error);
    }
  };


  let service = s.emp * s.ppm + s.events * s.eventFee + s.fixed + (s.edo ? 15_000 : 0) + (s.zup ? 25_000 : 0);
  if (s.sla) service = Math.round(service * 1.15);
  const saving = Math.max(0, s.current - service);
  const savingPct = s.current > 0 ? Math.round((saving / s.current) * 100) : 0;

  return (
    <main className="bg-white text-slate-900">
      <section className="mt-20 bg-gradient-to-br from-[#00214F] to-[#003B9B] text-white pt-24 pb-20 box-border">
        <div className="mx-auto max-w-6xl pl-1 pr-6">
          <h1 className="text-4xl font-black md:text-5xl">Аутстаффинг функций</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95 leading-tight">
            Передача HR-операций внешней команде: helpdesk HR, кадровое делопроизводство, табели/графики, ЭДО и 1С:ЗУП,
            расчет заработной платы (BPO). Работа по SLA, отчётность и комплаенс ТК РФ. Это <b>аутсорсинг функций (BPO)</b>, а не «заёмный труд».
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              asChild
              className="w-full sm:w-auto inline-block bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-white hover:bg-[#FF7C22]"
            >
              <a href="#calc" className="flex items-center justify-center h-full">
                Рассчитать стоимость
              </a>
            </Button>
            <Button
              asChild
              className="w-full sm:w-auto inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-[#003B9B] hover:bg-white"
            >
              <a href="#scope" className="flex items-center justify-center h-full">
                Что берём на себя
              </a>
            </Button>
          </div>
          <div className="mt-7 rounded-2xl border border-white/30 bg-white/10 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPI icon={<span>🚀</span>} value="2–4 недели" label="запуск пилота" />
              <KPI icon={<span>📨</span>} value="≥ 95%" label="тикетов в срок SLA" />
              <KPI icon={<span>💰</span>} value="−20–35%" label="снижение HR-OPEX*" />
              <KPI icon={<span>🧾</span>} value="ЭДО/1С" label="подписи, обмен и архив" />
            </div>
          </div>
        </div>
      </section>

      <Section id="scope" gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card>
            <h3 className="text-xl font-extrabold">Операционный блок</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Helpdesk HR: приём и решения заявок сотрудников</li>
              <li>КДП: приказы, отпуска, больничные, справки</li>
              <li>Табели, графики смен, сверки</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Системы и документы</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>ЭДО (Диадок/СБИС и др.), КЭП</li>
              <li>1С:ЗУП — обмен данными, расчёты (по договорённости)</li>
              <li>Архив, поиск, контроль версий</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Комплаенс</h3>
            <p className="mt-3 text-base text-black">
              Работаем по договору оказания услуг (BPO): задачи и результат под нашей ответственностью. Не допускаем фиктивный
              «заёмный труд» и прямое управление сотрудниками INTERIM со стороны заказчика.
            </p>
          </Card>
        </div>
      </Section>

      <Section id="calc">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Калькулятор стоимости</h3>
            <p className="mt-1 text-xs text-slate-500">
              Оценка по модели: стоимость за рабочее место + стоимость за мероприятие + фиксированная стоимость интеграции. Уточняется после диагностики.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <LabeledInputNumber label="Численность, чел." value={s.emp} onChange={(v) => setS({ ...s, emp: v })} min={1} />
              <LabeledInputNumber label="Текущие расходы HR, ₽/мес" value={s.current} onChange={(v) => setS({ ...s, current: v })} min={0} />
              <LabeledInputNumber label="Ставка, ₽/чел./мес" value={s.ppm} onChange={(v) => setS({ ...s, ppm: v })} min={0} />
              <LabeledInputNumber label="Событий/мес (приказы/справки)" value={s.events} onChange={(v) => setS({ ...s, events: v })} min={0} />
              <LabeledInputNumber label="Цена 1 события, ₽" value={s.eventFee} onChange={(v) => setS({ ...s, eventFee: v })} min={0} />
              <LabeledInputNumber label="Фикс интеграций, ₽/мес" value={s.fixed} onChange={(v) => setS({ ...s, fixed: v })} min={0} />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={s.edo}
                  onChange={(e) => setS({ ...s, edo: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                ЭДО/КЭП (+15 000 ₽)
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={s.zup}
                  onChange={(e) => setS({ ...s, zup: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                1С:ЗУП поддержка (+25 000 ₽)
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={s.sla}
                  onChange={(e) => setS({ ...s, sla: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                SLA Premium (+15%)
              </label>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <StatCard label="Текущие расходы" value={cf.format(s.current)} />
              <StatCard label="Сервис INTERIM" value={cf.format(service)} />
              <StatCard label="Экономия, ₽/мес" value={cf.format(saving)} />
              <StatCard label="Экономия, %" value={`${savingPct}%`} />
            </div>
            <p className="mt-2 text-xs text-slate-500">Итоговая смета зависит от процессов, интеграций и SLA.</p>
          </Card>

          <Card>
            <h3 className="text-xl font-extrabold">Соглашение об уровне сервиса (SLA) и отчётность</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-base">
                <thead>
                  <tr className="text-left text-black">
                    <th className="border-b border-slate-100 px-2 py-2 font-bold">Метрика</th>
                    <th className="border-b border-slate-100 px-2 py-2 font-bold">Цель</th>
                    <th className="border-b border-slate-100 px-2 py-2 font-bold">Комментарий</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Первый ответ по заявке</td>
                    <td className="border-b border-slate-100 px-2 py-2">≤ 30 мин</td>
                    <td className="border-b border-slate-100 px-2 py-2">в часы поддержки (9–19, пн–пт)</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Решение P1/P2/P3</td>
                    <td className="border-b border-slate-100 px-2 py-2">4 ч / 1 р.д. / 3 р.д.</td>
                    <td className="border-b border-slate-100 px-2 py-2">по приоритету заявки</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Заявки в срок</td>
                    <td className="border-b border-slate-100 px-2 py-2">≥ 95%</td>
                    <td className="border-b border-slate-100 px-2 py-2">ежемес. отчёт</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-2">ЭДО/приказы</td>
                    <td className="px-2 py-2">ежедневно</td>
                    <td className="px-2 py-2">подпись, архив, поиск</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">SLA фиксируем в договоре/Приложении.</p>
          </Card>
        </div>
      </Section>

      <Section gray>
        <h2 className="mb-4 text-2xl font-black">Пакеты</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              name: 'Start',
              items: ['Helpdesk HR + КДП базовое', 'Еженед. отчёт', 'e-Подписи документов'],
              note: 'До 100 чел.',
            },
            {
              name: 'Growth',
              items: ['Все из Start', 'Табели и графики + 1С:ЗУП', 'SLA с приоритетами'],
              note: '100–500 чел.',
            },
            {
              name: 'Enterprise',
              items: ['Каталог HR-услуг', 'СКУД интеграция', 'Расширенная аналитика'],
              note: '500+ чел.',
            },
          ].map((p) => (
            <Card key={p.name}>
              <h3 className="text-xl font-black">{p.name}</h3>
              <ul className="mt-3 space-y-2 text-slate-700 list-disc pl-5">
                {p.items.map((i) => <li key={i}>{i}</li>)}
              </ul>
              <p className="mt-3 text-sm text-slate-500">{p.note}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section>
        <h2 className="mb-4 text-2xl font-black">Процесс перехода</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
          {[
            { n: '1. Диагностика', d: 'Карта процессов и SLA, источники данных.' },
            { n: '2. План перехода', d: 'RACI, регламенты, права доступа.' },
            { n: '3. Интеграции', d: 'ЭДО, 1С:ЗУП, СКУД, портал.' },
            { n: '4. Пилот', d: '1–2 подразделения, корректировки.' },
            { n: '5. Запуск', d: 'Обучение, отчётность и QBR.' },
            { n: '6. Контроль', d: 'Аудиты, улучшения, комплаенс.' },
          ].map((s) => (
            <Card key={s.n}>
              <h3 className="text-lg font-extrabold">{s.n}</h3>
              <p className="mt-1 text-base text-slate-700">{s.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Кейс</h3>
            <p className="mt-2 text-base text-black"><b>Задача:</b> снизить нагрузку на HR-операции и ускорить обработку заявок.</p>
            <p className="mt-1 text-base text-black"><b>Решение:</b> helpdesk HR + КДП + ЭДО + 1С:ЗУП, SLA и аналитика.</p>
            <p className="mt-1 text-base text-black"><b>Результат:</b> первый ответ 15–30 мин, ≥95% тикетов в срок, −25–30% HR-OPEX за 4 мес.</p>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">FAQ</h3>
            <details className="group mt-2 border-t border-slate-200 py-3" open>
              <summary className="cursor-pointer text-base font-bold">Это «заёмный труд»?</summary>
              <p className="mt-1 text-sm text-slate-700">
                Нет. Мы оказываем услуги (BPO): задачи и результат под нашей ответственностью. Нет прямого управления нашими
                сотрудниками со стороны заказчика.
              </p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Можно добавить 1С:ЗУП и ЭДО?</summary>
              <p className="mt-1 text-sm text-slate-700">Да. Настроим обмен, подписи, архив и сверки.</p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Нужна ли лицензия?</summary>
              <p className="mt-1 text-sm text-slate-700">На аутсорсинг функций — нет. Для «предоставления персонала» действуют иные правила.</p>
            </details>
          </Card>
        </div>
      </Section>

      <Section id="cta">
        <Card>
          <h3 className="text-xl font-extrabold">Запросить аутстаффинг функций</h3>
          <form
            onSubmit={handleSubmit}
            className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Имя</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Как к вам обращаться"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Телефон</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="+7 () ___--__--__"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-slate-500">Какие функции</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Например: helpdesk HR + КДП + ЭДО"
                name="userFunctions"
                value={formData.userFunctions}
                onChange={handleInputChange}
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-slate-500">Email</span>
              <input
                type="email"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="for@example.com"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </label>

            <label className="flex items-start text-xs sm:text-sm cursor-pointer text-slate-600 sm:col-span-2">
              <input
                type="checkbox"
                checked={formData.agreed}
                onChange={(e) => handleAgreementChange(e.target.checked)}
                className="mt-1 mr-2 h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]"
                required
              />
              <span>
                Я даю <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">согласие на обработку персональных данных</a> и соглашаюсь с <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">Политикой конфиденциальности</a>.
              </span>
            </label>

            <div className="sm:col-span-2">
              <button
                type="submit"
                className="inline-block rounded-xl px-6 py-3 text-sm font-bold text-white shadow transition-all duration-200 bg-[#FF7C22] hover:brightness-[105%] hover:-translate-y-[1px]"
              >
                Отправить
              </button>
              {formStatus && <p className={getStatusClasses(formStatus)}>{formStatus}</p>}
            </div>
          </form>
          <p className="mt-2 text-xs text-slate-500">По запросу направим матрицу SLA и чек-лист перехода.</p>
        </Card>
      </Section>

    </main>
  );
}
